import { useEffect, useState } from 'react';
import getBookingEventHistory from '../../services/bookings/getBookingEventHistory';
import { BookingEventLog, BookingEventHistory } from '../../services/bookings/types'; // Assuming types are in a file
import { DetailsSection, DetailsSectionHeading } from '../shared/utils';
import { RowDefinitionDetails } from '../../components';

type EventHistoryProps = {
  bookingId: string;
};

function EventHistory({ bookingId }: EventHistoryProps) {
  // 1. State for storing the fetched data
  const [eventHistory, setEventHistory] = useState<BookingEventHistory | null>(null);
  // 2. State for handling the loading status
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // 3. State for handling potential errors
  const [error, setError] = useState<string | null>(null);

  // The useEffect hook handles the data fetching side effect.
  useEffect(() => {
    // We define an async function inside the effect to use await.
    const fetchEventHistory = async () => {
      try {
        setIsLoading(true); // Signal that we are starting to fetch
        setError(null); // Clear any previous errors

        const data = await getBookingEventHistory(bookingId);
        setEventHistory(data); // Store the successful response in state
      } catch (err) {
        console.error('Failed to fetch event history:', err);
        setError('Could not load the booking history.'); // Store the error message
      } finally {
        setIsLoading(false); // Signal that fetching is complete (either success or fail)
      }
    };

    fetchEventHistory();
  }, [bookingId]); // The dependency array: this effect will re-run if bookingId changes.

  if (isLoading) {
    return <div className="min-h-screen pt-7">Loading...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen pt-7" style={{ color: 'red' }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-7">
      <DetailsSection>
        <DetailsSectionHeading>Event History</DetailsSectionHeading>
        <div className="border-t border-gray-200">
          <dl className="divide-y divide-gray-200">
            {eventHistory && eventHistory.eventLogs.length > 0 ? (
              <ul>
                {eventHistory.eventLogs.map((log: BookingEventLog) => (
                  <RowDefinitionDetails key={log.id} label={`${new Date(log.timeCreated).toLocaleString()}`}>
                    {log.action} {log.reason} traceId: {log.traceId}, {log.message ? `: ${log.message}` : ''}
                  </RowDefinitionDetails>
                  // <li key={log.id}>
                  //   <strong>{log.action}</strong>
                  // </li>
                ))}
              </ul>
            ) : (
              <p>No event history found for this booking.</p>
            )}
          </dl>
        </div>
      </DetailsSection>
    </div>
  );
}

export default EventHistory;
